<template>
  <div
    class="flex shrink-0 grow-0 items-center justify-center overflow-hidden rounded-full uppercase select-none"
    :class="[
      sizeClasses,
      borderClasses,
      shapeClassesForPerspective(perspective, allPerspectives),
      colorClassesForPerspective(perspective, allPerspectives),
    ]"
    v-tooltip="memberFullName(member)"
  >
    <img v-if="hasImage" class="h-full w-full" :src="avatarUrl!" alt="" />
    <span v-else>
      <template v-if="includeLastName">{{ memberInitials(member) }}</template>
      <template v-else>{{ memberInitials(member)[0] }}</template>
    </span>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useAvatarStore } from '../../../../stores/avatar.store.ts'
  import type { AvatarMemberAttributes, AvatarSize } from './avatar-props.ts'
  import { memberFullName, memberInitials } from '@/core/models/member/member-name'
  import {
    colorClassesForPerspective,
    shapeClassesForPerspective,
  } from '@/core/models/feedback-perspective.ts'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective.ts'

  interface Props {
    member: AvatarMemberAttributes
    allPerspectives: ReadonlyArray<Perspective>
    perspective: Perspective | string
    includeLastName?: boolean
    size?: AvatarSize
    omitTooltip?: boolean
  }

  const { size = '40', member, perspective, allPerspectives } = defineProps<Props>()

  const avatarStore = useAvatarStore()
  const isSelf = computed(() => perspective === SELF_PERSPECTIVE_KEY)

  const sizeClasses = computed(() => {
    if (size == '160') {
      return 'h-40 w-40 text-display-large'
    }
    if (size == '128') {
      return 'h-32 w-32 text-title-large'
    }
    if (size == '96') {
      return 'h-24 w-24 text-title-large'
    }
    if (size == '80') {
      return 'h-20 w-20 text-headline-large'
    }
    if (size == '64') {
      return 'h-16 w-16 text-title-large'
    }
    if (size == '56') {
      return 'h-14 w-14 text-title-large'
    }
    if (size == '32') {
      return 'h-8 w-8 text-label-large'
    }
    if (size == '24') {
      return 'h-6 w-6 text-label-small'
    }
    if (size == '20') {
      return 'h-5 w-5 text-label-small'
    }
    return 'h-10 w-10 text-label-large'
  })

  const borderClasses = computed(() => {
    if (isSelf.value) {
      if (parseInt(size) > 32) {
        return `border-2 border-primary-container`
      }
      return `border-1 border-primary-container`
    }
    if (parseInt(size) > 32) {
      return `border-2`
    }
    return `border-1`
  })

  const avatarUrl = computed(() => {
    if (member.avatarFileContent) {
      return `data:image/png;base64,${member.avatarFileContent}`
    }
    return avatarStore.getAvatarForMember(member.id, member.lastUpdatedAvatar).value
  })

  const hasImage = computed(() => member.lastUpdatedAvatar && avatarUrl.value)
</script>
